<script setup lang="ts">
import { ref, watch } from 'vue'
import { useLiveOpsBridge } from '@/composables/game/live-ops-bridge'
import LiveOpsPaymentModal from '@/components/modals/LiveOpsPaymentModal.vue'
import MineEarnedOverlay from '@/components/overlays/MineEarnedOverlay.vue'
import { useLiveOpsOverlay } from '@/composables/game/live-ops-overlay'
import { useMultiplayerMine } from '@/composables/game/multiplayer-mine'

const props = defineProps<{
  show: boolean
  eventId: string
}>()

const emit = defineEmits<{
  hide: []
}>()

function onClose() {
  emit('hide')
}

const {
  isLoading,
  error,
  iframeUrl,
  setIframeRef,
  onIframeLoad,
  pendingPayment,
  handlePaymentComplete,
  handlePaymentCancel,
  liveOpsUserEvent,
  pendingMineEarned,
} = useLiveOpsBridge(props.eventId, { onClose })

const { closeLiveOpsOverlay } = useLiveOpsOverlay()
const { openPlantMineModal } = useMultiplayerMine()

const iframeElement = ref<HTMLIFrameElement | null>(null)

watch(iframeElement, (el) => {
  setIframeRef(el)
})

watch(() => props.show, (val) => {
  if (!val) pendingMineEarned.value = 0
})

function onPlaceNow() {
  pendingMineEarned.value = 0
  closeLiveOpsOverlay()
  openPlantMineModal()
}

function onDismiss() {
  pendingMineEarned.value = 0
}
</script>

<template>
  <div class="fixed inset-0 w-full h-full bg-black flex flex-col z-[800]">
    <!-- Error State -->
    <div
      v-if="error"
      class="absolute inset-0 flex flex-col items-center justify-center bg-black z-10"
    >
      <div class="flex flex-col items-center p-6">
        <div class="size-16 rounded-full bg-red-500 text-white text-3xl font-bold flex items-center justify-center mb-4">
          !
        </div>
        <p class="text-white text-base text-center max-w-[300px]">
          {{ error }}
        </p>
      </div>
    </div>

    <!-- Main Content -->
    <template v-else>
      <!-- Loading Spinner -->
      <div
        v-if="isLoading"
        class="absolute inset-0 flex flex-col items-center justify-center bg-black z-10"
      >
        <div class="size-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
        <p class="mt-4 text-white text-base">
          Loading...
        </p>
      </div>

      <!-- Iframe -->
      <iframe
        v-show="!isLoading"
        ref="iframeElement"
        :src="iframeUrl"
        class="w-full h-full border-none flex-1"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        allow="clipboard-write"
        @load="onIframeLoad"
      />

      <!-- Mine Earned Overlay -->
      <MineEarnedOverlay
        :show="pendingMineEarned > 0"
        :count="pendingMineEarned"
        @place-now="onPlaceNow"
        @dismiss="onDismiss"
      />

      <!-- Payment Modal -->
      <LiveOpsPaymentModal
        v-if="pendingPayment"
        :show="!!pendingPayment"
        :payment-info="pendingPayment"
        :user-event-id="liveOpsUserEvent?.id"
        @purchase-done="handlePaymentComplete"
        @hide="handlePaymentCancel"
      />
    </template>
  </div>
</template>
